import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useRoom } from "./RoomProvider";
import { useLiveDrivers } from "../../hooks/useLiveDrivers";

/**
 * ProvidersPage — full-screen roster, replaces the old cramped
 * top-left ProviderChips dock. Opened from the "providers" button in the
 * top bar (`/floor/providers`), not squeezed into the left rail anymore —
 * that list only grows, a fixed-width chip row was never going to scale.
 * Collapsible "online" / "offline" groups + a name filter so it stays
 * usable at any roster size.
 */
export default function ProvidersPage() {
  const { allTrips = [], realProviders = [], focusOn } = useRoom();
  const { drivers = [] } = useLiveDrivers({ pollMs: 15000 });
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [offlineOpen, setOfflineOpen] = useState(false);

  const rows = useMemo(() => {
    const byTp = new Map();
    (realProviders || []).forEach(p => {
      if (p.status && p.status !== "active") return; // archived/rejected — not an operating provider
      byTp.set(p.tp_id, { tp_id: p.tp_id, name: p.name || p.company_name, online: 0, on_trip: 0 });
    });
    drivers.forEach(d => {
      const tpId = d.tp_id || "unknown";
      const e = byTp.get(tpId) || { tp_id: tpId, name: d.tp_name || tpId, online: 0, on_trip: 0 };
      e.online += 1;
      if (["loaded", "on_trip", "en_route"].includes(String(d.shift_status).toLowerCase())) e.on_trip += 1;
      byTp.set(tpId, e);
    });
    (allTrips || []).forEach(t => {
      const tp = t.assigned_tp;
      if (!tp?.tp_id) return;
      const e = byTp.get(tp.tp_id) || { tp_id: tp.tp_id, name: tp.tp_name, online: 0, on_trip: 0 };
      if (!e.name && tp.tp_name) e.name = tp.tp_name;
      byTp.set(tp.tp_id, e);
    });
    return Array.from(byTp.values()).sort((a, b) => (b.online || 0) - (a.online || 0));
  }, [drivers, allTrips, realProviders]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r => String(r.name || r.tp_id).toLowerCase().includes(q));
  }, [rows, query]);

  const online = filtered.filter(r => r.online > 0);
  const offline = filtered.filter(r => r.online === 0);

  const openProvider = (tpId) => {
    if (focusOn) focusOn("provider", tpId);
    navigate("/floor");
  };

  return (
    <div className="providers-page" data-testid="providers-page">
      <div className="pp-header">
        <button className="pp-back" onClick={() => navigate("/floor")} data-testid="providers-page-back-btn">
          ← back to floor
        </button>
        <h1 className="pp-title">Providers</h1>
        <span className="pp-count" data-testid="providers-page-count">{rows.length} total</span>
        <input
          className="pp-search"
          placeholder="filter by name…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          data-testid="providers-page-search-input"
        />
      </div>

      <div className="pp-body">
        <ProviderGroup
          label={`ONLINE NOW · ${online.length}`}
          rows={online}
          open
          onToggle={null}
          onOpenProvider={openProvider}
          emptyText="No providers currently have a driver online."
        />
        <ProviderGroup
          label={`OFFLINE · ${offline.length}`}
          rows={offline}
          open={offlineOpen}
          onToggle={() => setOfflineOpen(v => !v)}
          onOpenProvider={openProvider}
          emptyText="Every active provider has at least one driver online."
        />
      </div>
    </div>
  );
}

function ProviderGroup({ label, rows, open, onToggle, onOpenProvider, emptyText }) {
  return (
    <section className="pp-group">
      <button
        className={`pp-group-header ${onToggle ? "pp-group-header--toggle" : ""}`}
        onClick={onToggle || undefined}
        data-testid={`providers-group-${label.split(" ")[0].toLowerCase()}`}
      >
        <span className={`pp-chevron ${open ? "pp-chevron--open" : ""}`}>{onToggle ? "▸" : ""}</span>
        {label}
      </button>
      {open && (
        rows.length === 0 ? (
          <div className="pp-empty">{emptyText}</div>
        ) : (
          <div className="pp-grid">
            {rows.map(r => {
              const health = r.online === 0 ? "off" : r.online < 5 ? "low" : "ok";
              return (
                <button
                  key={r.tp_id}
                  className={`pp-card pp-card--${health}`}
                  onClick={() => onOpenProvider(r.tp_id)}
                  data-testid={`providers-page-card-${r.tp_id}`}
                >
                  <span className="pp-card-name">{r.name || r.tp_id}</span>
                  <span className="pp-card-stats">
                    <span className="pp-card-online">{r.online} online</span>
                    {r.on_trip > 0 && <span className="pp-card-ontrip">· {r.on_trip} on trip</span>}
                  </span>
                </button>
              );
            })}
          </div>
        )
      )}
    </section>
  );
}
